// Fail-soft recovery for legacy plain `$...` placeholder syntax.
// Example: `.m-$i` becomes `.m-${i}` when debug mode is disabled.
import type { FailSoftRecoveryResult } from './fail-soft-types'

// Build a recovery source for plain `$name` placeholder syntax errors.
/** @internal Build a recovery source for plain `$name` placeholder syntax errors. */
export function buildLegacyPlaceholderRecoverySource(source: string, error: unknown): FailSoftRecoveryResult | null {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  // Check whether the current error is a plain-placeholder syntax failure.
  if (!message.includes('plain loop placeholder')) {
    // Return null for unrelated compile errors.
    return null
  }
  const recoveredSource = rewriteLegacyPlaceholders(source)
  // Check whether the recovery pass changed the source at all.
  if (recoveredSource === source) {
    // Return null when no plain placeholders were rewritten.
    return null
  }
  // Return the legacy-placeholder recovery payload.
  return {
    _source: recoveredSource,
    _line: 1,
    _message: 'Fail-soft recovery rewrote legacy plain `$...` placeholders to `${...}`.',
  }
}

// Rewrite plain `$name` references into `${name}` outside comments and strings.
export function rewriteLegacyPlaceholders(source: string): string {
  let output = ''
  let quote: "'" | '"' | null = null
  let inLineComment = false
  let inBlockComment = false
  // Scan source text and rewrite only safe placeholder candidates.
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]
    const next = source[index + 1] ?? ''
    const previous = index > 0 ? source[index - 1] : ''
    // Check line-comment state before placeholder rewriting.
    if (inLineComment) {
      output += char
      if (char === '\n') inLineComment = false
      continue
    }
    // Check block-comment state before placeholder rewriting.
    if (inBlockComment) {
      output += char
      if (char === '*' && next === '/') {
        output += next
        inBlockComment = false
        index += 1
      }
      continue
    }
    // Check quoted-string state before placeholder rewriting.
    if (quote) {
      output += char
      if (char === quote && previous !== '\\') quote = null
      continue
    }
    // Check comment-entry tokens before placeholder rewriting.
    if (char === '/' && next === '*') {
      output += '/*'
      inBlockComment = true
      index += 1
      continue
    }
    if (char === '/' && next === '/') {
      output += '//'
      inLineComment = true
      index += 1
      continue
    }
    // Check quote-entry tokens before placeholder rewriting.
    if (char === "'" || char === '"') {
      quote = char
      output += char
      continue
    }
    // Check whether the current `$` starts a plain placeholder reference.
    if (char === '$' && next !== '{' && isIdentifierStart(next)) {
      const end = readReferenceEnd(source, index + 1, previous === '.' || previous === '-' || previous === '_' || previous === ':')
      const token = source.slice(index + 1, end)
      // Check whether the matched token is a local declaration head.
      if (isDeclarationToken(source, end)) {
        output += `$${token}`
      } else {
        // Rewrite plain placeholder usage to braced syntax.
        output += `\${${token}}`
      }
      index = end - 1
      continue
    }
    output += char
  }
  // Return the rewritten source text.
  return output
}

// Read one plain placeholder token body after the leading `$`.
function readReferenceEnd(source: string, start: number, selectorLike = false): number {
  let end = start + 1
  let sawDot = false
  // Continue token scanning across dotted identifier segments.
  while (end < source.length) {
    const char = source[end]
    if (isIdentifierBody(char) || char === '.') {
      if (char === '.') sawDot = true
      end += 1
      continue
    } else if (char === '-') {
      if (selectorLike && sawDot) break
      const next = source[end + 1] ?? ''
      // Keep hyphen only when it continues a real identifier segment.
      if (isIdentifierBody(next)) {
        end += 1
        continue
      } else {
        // Stop token scan on selector or placeholder boundary hyphens.
        break
      }
    }
    break
  }
  // Return the first index after the placeholder token.
  return end
}

// Check whether the scanned `$name` token is a local declaration head.
function isDeclarationToken(source: string, index: number): boolean {
  let cursor = index
  // Skip inline spaces before declaration-colon detection.
  while (cursor < source.length && (source[cursor] === ' ' || source[cursor] === '\t')) {
    cursor += 1
  }
  // Return true only for `$name:` local declaration syntax.
  return source[cursor] === ':'
}
// Check whether a char can start a placeholder identifier.
function isIdentifierStart(char: string): boolean {
  // Return true for alphabetic and underscore starters.
  return (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z') || char === '_'
}
// Check whether a char can continue a placeholder identifier.
function isIdentifierBody(char: string): boolean {
  // Return true for identifier body chars and digits.
  return isIdentifierStart(char) || (char >= '0' && char <= '9')
}
